import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FiChevronLeft, FiChevronRight, FiMaximize2, FiX } from 'react-icons/fi';
import { getImageUrl } from '../../utils/api';
import { expertTheme, focusRing, reducedMotion } from '../../theme/expertTheme';

const NOFOTO = '/nofoto.png';

const Wrapper = styled.div`
  min-width: 0;
`;

const PhotoBlock = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${expertTheme.radius.large};
  background: #dedbd2;
  aspect-ratio: 4 / 5;
  box-shadow: ${expertTheme.shadow.soft};
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center center;
  cursor: zoom-in;
  transition: transform 420ms ease;
  ${reducedMotion}

  ${PhotoBlock}:hover & { transform: scale(1.018); }
`;

const Placeholder = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  background: linear-gradient(145deg, #e8e5dd, #d8d4ca);

  img { width: 120px; opacity: 0.26; }
`;

const MediaHint = styled.span`
  position: absolute;
  left: 18px;
  bottom: 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 8px 14px;
  border-radius: ${expertTheme.radius.pill};
  color: #fff;
  background: rgba(28, 29, 26, 0.72);
  backdrop-filter: blur(12px);
  font-size: 13px;
  font-weight: 600;
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  ${p => p.$side === 'left' ? 'left: 16px;' : 'right: 16px;'}
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: ${expertTheme.colors.ink};
  box-shadow: 0 8px 24px rgba(0,0,0,.14);
  opacity: 0;
  transition: opacity 180ms ease, background 180ms ease;
  z-index: 2;
  ${focusRing}
  ${reducedMotion}

  ${PhotoBlock}:hover & { opacity: 1; }
  &:hover { background: #fff; }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    opacity: 1;
  }
`;

const DotsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding-top: 16px;
`;

const Dot = styled.button`
  width: ${p => (p.$active ? '28px' : '10px')};
  height: 10px;
  border-radius: ${expertTheme.radius.pill};
  background: ${p => (p.$active ? expertTheme.colors.accent : '#c9c5bc')};
  transition: width 180ms ease, background 180ms ease;
  ${focusRing}
  ${reducedMotion}
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: grid;
  place-items: center;
  padding: 56px;
  background: rgba(15, 16, 14, 0.94);
  backdrop-filter: blur(10px);

  @media (max-width: 768px) { padding: 64px 16px 24px; }
`;

const LightboxContent = styled.div`
  position: relative;
  max-width: min(1100px, 90vw);
  max-height: 88vh;
`;

const LightboxImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 88vh;
  border-radius: ${expertTheme.radius.medium};
  object-fit: contain;
`;

const LightboxButton = styled.button`
  position: absolute;
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  color: #fff;
  border-radius: 50%;
  background: rgba(255,255,255,.14);
  ${focusRing}

  &.close { top: -56px; right: 0; }
  &.prev { top: 50%; left: -64px; transform: translateY(-50%); }
  &.next { top: 50%; right: -64px; transform: translateY(-50%); }

  @media (max-width: 768px) {
    &.prev { left: 10px; }
    &.next { right: 10px; }
  }
`;

function resolveImage(path) {
  if (!path) return null;
  return getImageUrl(path) || null;
}

export function buildExpertGallery(expert) {
  if (!expert) return [];
  let gallery = expert.gallery_urls ?? expert.galleryUrls;
  if (typeof gallery === 'string') {
    try { gallery = JSON.parse(gallery); } catch { gallery = []; }
  }
  if (!Array.isArray(gallery)) gallery = [];

  const merged = [];
  const add = (url) => {
    if (!url || typeof url !== 'string') return;
    const trimmed = url.trim();
    if (trimmed && !merged.includes(trimmed)) merged.push(trimmed);
  };

  add(expert.photo_url ?? expert.photoUrl);
  gallery.forEach(add);
  return merged.map(resolveImage).filter(Boolean);
}

const ExpertPhotoGallery = ({ expert, alt = 'Эксперт' }) => {
  const images = useMemo(() => buildExpertGallery(expert), [expert]);
  const [index, setIndex] = useState(0);
  const [failedIndices, setFailedIndices] = useState(() => new Set());
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const workingImages = useMemo(
    () => images.filter((_, imageIndex) => !failedIndices.has(imageIndex)),
    [images, failedIndices]
  );
  const safeIndex = workingImages.length ? Math.min(index, workingImages.length - 1) : 0;
  const currentSrc = workingImages[safeIndex] || null;
  const hasMultiple = workingImages.length > 1;

  const goPrev = useCallback(() => {
    if (workingImages.length <= 1) return;
    setIndex(current => current === 0 ? workingImages.length - 1 : current - 1);
  }, [workingImages.length]);

  const goNext = useCallback(() => {
    if (workingImages.length <= 1) return;
    setIndex(current => current === workingImages.length - 1 ? 0 : current + 1);
  }, [workingImages.length]);

  useEffect(() => {
    setIndex(0);
    setFailedIndices(new Set());
    setLightboxOpen(false);
  }, [expert?.id, images.length]);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setLightboxOpen(false);
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [goNext, goPrev, lightboxOpen]);

  const handleImageError = useCallback((src) => {
    const originalIndex = images.indexOf(src);
    if (originalIndex < 0) return;
    setFailedIndices(previous => new Set(previous).add(originalIndex));
  }, [images]);

  return (
    <Wrapper>
      <PhotoBlock>
        {currentSrc ? (
          <MainImage
            src={currentSrc}
            alt={`${alt} — фото ${safeIndex + 1}`}
            onClick={() => setLightboxOpen(true)}
            onError={() => handleImageError(currentSrc)}
          />
        ) : (
          <Placeholder><img src={NOFOTO} alt="Нет фотографии эксперта" /></Placeholder>
        )}
        {currentSrc ? <MediaHint><FiMaximize2 aria-hidden="true" /> Открыть фото</MediaHint> : null}
        {hasMultiple ? (
          <>
            <NavButton type="button" $side="left" onClick={goPrev} aria-label="Предыдущее фото"><FiChevronLeft size={23} /></NavButton>
            <NavButton type="button" $side="right" onClick={goNext} aria-label="Следующее фото"><FiChevronRight size={23} /></NavButton>
          </>
        ) : null}
      </PhotoBlock>

      {hasMultiple ? (
        <DotsRow aria-label="Выбор фотографии">
          {workingImages.map((_, dotIndex) => (
            <Dot key={dotIndex} type="button" $active={dotIndex === safeIndex} onClick={() => setIndex(dotIndex)} aria-label={`Фото ${dotIndex + 1}`} />
          ))}
        </DotsRow>
      ) : null}

      {lightboxOpen && currentSrc ? (
        <LightboxOverlay role="dialog" aria-modal="true" aria-label={`Галерея: ${alt}`} onClick={() => setLightboxOpen(false)}>
          <LightboxContent onClick={event => event.stopPropagation()}>
            <LightboxButton type="button" className="close" onClick={() => setLightboxOpen(false)} aria-label="Закрыть"><FiX size={25} /></LightboxButton>
            {hasMultiple ? <LightboxButton type="button" className="prev" onClick={goPrev} aria-label="Предыдущее фото"><FiChevronLeft size={25} /></LightboxButton> : null}
            <LightboxImage src={currentSrc} alt={`${alt} — фото ${safeIndex + 1}`} />
            {hasMultiple ? <LightboxButton type="button" className="next" onClick={goNext} aria-label="Следующее фото"><FiChevronRight size={25} /></LightboxButton> : null}
          </LightboxContent>
        </LightboxOverlay>
      ) : null}
    </Wrapper>
  );
};

export default ExpertPhotoGallery;
